import { type LookResponse, LookResponseSchema } from '@ootopia/shared-contracts'
import { AppButton, LoadingSkeleton, StatusAlert } from '@ootopia/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/$creator/$slug')({
  component: LookPage,
})

function LookPage() {
  const { slug } = Route.useParams()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['look', slug],
    queryFn: async () => {
      const res = await fetch(`/api/v1/looks/${slug}`)
      if (!res.ok) throw new Error(`Look not found (${res.status})`)
      const json = await res.json()
      return LookResponseSchema.parse(json)
    },
  })

  const navigate = useNavigate()

  const createPlanMutation = useMutation({
    mutationFn: async () => {
      if (!data) throw new Error('Look data not loaded')
      // Try to create a remix plan; if 401, try dev-session then retry
      const res = await fetch('/api/v1/remix-plans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lookId: data.look.id }),
      })
      if (res.status === 401) {
        // Try dev session then retry
        const devRes = await fetch('/api/auth/dev-session', { method: 'POST' })
        if (devRes.ok) {
          const retryRes = await fetch('/api/v1/remix-plans', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lookId: data.look.id }),
          })
          if (!retryRes.ok) throw new Error('Failed to create plan')
          return retryRes.json()
        }
        throw new Error('Please sign in to start your remix')
      }
      if (!res.ok) throw new Error('Failed to create plan')
      return res.json()
    },
    onSuccess: (plan: { id: string }) => {
      navigate({ to: '/capture', search: { planId: plan.id, returnTo: undefined } })
    },
    onError: (err: unknown) => {
      // Show error as alert
      console.error('Failed to create plan:', err)
    },
  })

  const [isOnline, setIsOnline] = React.useState(navigator.onLine)

  React.useEffect(() => {
    const on = () => setIsOnline(true)
    const off = () => setIsOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  const [LookCard, setLookCard] = React.useState<React.ComponentType<{
    look: LookResponse['look']
    slots: LookResponse['slots']
  }> | null>(null)

  React.useEffect(() => {
    import('@ootopia/ui').then((m) => {
      setLookCard(() => m.LookCard as never)
    })
  }, [])

  if (isLoading) {
    return React.createElement(
      'div',
      { className: 'flex flex-col gap-4' },
      React.createElement(LoadingSkeleton, { className: 'h-48 w-full rounded-xl' } as never),
      React.createElement(LoadingSkeleton, { className: 'h-6 w-3/4 rounded' } as never),
      React.createElement(LoadingSkeleton, { className: 'h-4 w-1/2 rounded' } as never),
      React.createElement(LoadingSkeleton, { className: 'h-20 w-full rounded-xl' } as never),
    )
  }

  if (error) {
    return React.createElement(
      'div',
      { className: 'flex flex-col items-center gap-3 py-12' },
      React.createElement(StatusAlert, {
        color: 'danger',
        title: 'Look not found',
        children: error.message,
      } as never),
      React.createElement(
        AppButton,
        { color: 'primary', onPress: () => refetch() } as never,
        'Retry',
      ),
    )
  }

  if (!isOnline && !data) {
    return React.createElement(
      'div',
      { className: 'flex flex-col items-center gap-3 py-12' },
      React.createElement(StatusAlert, {
        color: 'warning',
        title: "You're offline",
        children: 'Check your connection and try again.',
      } as never),
    )
  }

  if (!data) return null

  if (data.slots.length === 0) {
    return React.createElement(
      'div',
      { className: 'flex flex-col items-center gap-3 py-12' },
      React.createElement(StatusAlert, {
        color: 'warning',
        title: 'No products listed',
        children: 'This look has no product details yet.',
      } as never),
    )
  }

  return React.createElement(
    'div',
    { className: 'flex flex-col gap-4 pb-20' },
    LookCard
      ? React.createElement(LookCard as never, { look: data.look, slots: data.slots } as never)
      : React.createElement(LoadingSkeleton, { className: 'h-64 w-full rounded-xl' } as never),
    React.createElement(
      'div',
      {
        className:
          'sticky bottom-0 left-0 right-0 z-10 ootopia-glass border-t border-border/50 p-4 -mx-4',
      },
      React.createElement(
        AppButton,
        {
          color: 'primary',
          fullWidth: true,
          onPress: () => createPlanMutation.mutate(),
          isDisabled: createPlanMutation.isPending,
        } as never,
        'Remix for My Skin + My Kit',
      ),
    ),
  )
}
